/**
 * LashDemoContent - Demo content for Lash technician invites
 * Violet-purple color scheme matching PILOT_CONFIGS
 * All text fields are editable via CMS (EditableText)
 * All color blocks are editable images for super admin
 * Includes: Acuity booking flow, service menu with images, preference card mockup
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useAdmin, EditableText, EditableImage, EditableSection } from "@/components/AdminContentEditor";
import {
  ArrowRight,
  Check,
  Eye,
  Clock,
  AlertCircle,
  Image as ImageIcon,
  Heart,
  Calendar,
  Star,
  Sparkles,
  Users,
  Shield,
  MessageCircle,
  Send,
  Smartphone,
  ClipboardList
} from "lucide-react";

export default function LashDemoContent({ onAccept, portfolioImages = [] }) {
  const { editMode } = useAdmin();

  // Lash services with image support
  const services = [
    { id: "classic-full", name: "Classic Full Set", duration: "2 hrs", price: "$120+" },
    { id: "volume-full", name: "Volume Full Set", duration: "2.5 hrs", price: "$180+" },
    { id: "hybrid-full", name: "Hybrid Full Set", duration: "2 hrs", price: "$150+" },
    { id: "mega-volume", name: "Mega Volume", duration: "3 hrs", price: "$220+" },
    { id: "lash-fill-2wk", name: "Lash Fill (2 weeks)", duration: "1 hr", price: "$65+" },
    { id: "lash-fill-3wk", name: "Lash Fill (3 weeks)", duration: "1.5 hrs", price: "$85+" },
    { id: "lash-lift-tint", name: "Lash Lift & Tint", duration: "1 hr", price: "$80+" },
    { id: "lash-removal", name: "Lash Removal", duration: "30 min", price: "$30" }
  ];

  // Lash style options with editable images
  const lashStyles = [
    { id: "natural", name: "Natural" },
    { id: "classic", name: "Classic" },
    { id: "cat-eye", name: "Cat Eye" },
    { id: "doll-eye", name: "Doll Eye" },
    { id: "wispy", name: "Wispy" },
    { id: "dramatic", name: "Dramatic" }
  ];

  // Features
  const features = [
    {
      title: "Eye Shape & Mapping",
      desc: "Client shares their eye shape, and you can plan the perfect lash map before the appointment"
    },
    {
      title: "Allergy & Sensitivity Tracking",
      desc: "Know about adhesive sensitivities, eye allergies, or contact lens wearers ahead of time"
    },
    {
      title: "Style Preference Library",
      desc: "Clients browse your portfolio and select their desired lash style with inspo photos"
    },
    {
      title: "Aftercare Reminders",
      desc: "Automated reminders for fill appointments and aftercare instructions"
    },
    {
      title: "Curl & Length Preferences",
      desc: "Clients specify their preferred curl type, length, and thickness before arriving"
    },
    {
      title: "Fill Interval Tracking",
      desc: "Know their typical fill schedule and retention patterns for better planning"
    }
  ];

  // Eye shapes for client preferences
  const eyeShapes = ["Almond", "Round", "Hooded", "Monolid", "Downturned", "Upturned"];

  // Curl types
  const curlTypes = ["J Curl", "B Curl", "C Curl", "CC Curl", "D Curl", "L Curl"];

  // Length options
  const lengthOptions = ["Natural (8-10mm)", "Medium (10-12mm)", "Long (12-14mm)", "Extra Long (14-16mm)"];

  // Booking flow steps - Acuity integration flow
  const flowSteps = [
    { num: 1, icon: Calendar, title: "Book on Acuity", desc: "Client books their lash appointment through your Acuity or booking system" },
    { num: 2, icon: Send, title: "Preference Prompt", desc: "Auto-text with phygital link to share eye shape, style & inspo" },
    { num: 3, icon: ClipboardList, title: "You Review", desc: "See their eye shape, curl preference, inspo photos & sensitivities" },
    { num: 4, icon: Sparkles, title: "Day Of", desc: "Client arrives, you're prepped with the perfect lash map" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <EditableSection sectionId="lash-demo-hero" label="Lash Hero" className="py-16 px-4 bg-gradient-to-b from-violet-50 to-white">
        <div className="max-w-6xl mx-auto text-center">
          <Badge className="mb-4 bg-violet-100 text-violet-700" data-testid="lash-demo-badge">
            <Eye className="w-3 h-3 mr-1" />
            <EditableText
              sectionId="lash-demo-hero"
              field="badge"
              defaultValue="Lash Tech Pilot by Phygital"
              className="inline"
            />
          </Badge>

          <EditableText
            sectionId="lash-demo-hero"
            field="title"
            defaultValue="Know Their Ideal Look Before They Close Their Eyes"
            className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900"
            as="h1"
          />

          <EditableText
            sectionId="lash-demo-hero"
            field="subtitle"
            defaultValue="Clients share their eye shape, style preferences, and inspo photos before the appointment. You arrive prepared with the perfect lash map."
            className="text-gray-600 max-w-3xl mx-auto text-lg mb-8"
            as="p"
          />

          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {["Eye shape mapping", "Style library", "Allergy tracking", "Fill reminders"].map(tag => (
              <Badge key={tag} variant="secondary" className="bg-white border">
                {tag}
              </Badge>
            ))}
          </div>

          <Button onClick={onAccept} className="bg-gradient-to-r from-violet-500 to-purple-500 text-white rounded-full px-8 h-12" data-testid="lash-hero-cta">
            <EditableText
              sectionId="lash-demo-hero"
              field="cta_text"
              defaultValue="Accept Invite"
              className="inline"
            />
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-6 mt-12 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <EditableText sectionId="lash-demo-hero" field="trust_1" defaultValue="Free during pilot" className="inline" />
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <EditableText sectionId="lash-demo-hero" field="trust_2" defaultValue="Limited spots" className="inline" />
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              <EditableText sectionId="lash-demo-hero" field="trust_3" defaultValue="Shape the platform" className="inline" />
            </div>
          </div>
        </div>
      </EditableSection>

      {/* Booking Flow - Acuity Integration */}
      <EditableSection sectionId="lash-demo-flow" label="Booking Flow" className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-3 bg-violet-100 text-violet-700">
              <EditableText sectionId="lash-demo-flow" field="badge" defaultValue="Works with Acuity & Your Booking System" className="inline" />
            </Badge>
            <EditableText
              sectionId="lash-demo-flow"
              field="title"
              defaultValue="From Booked to Prepped"
              className="text-3xl font-bold mb-2 block"
              as="h2"
            />
            <EditableText
              sectionId="lash-demo-flow"
              field="subtitle"
              defaultValue="Keep using your current booking system. Phygital adds the preference layer so you know exactly what to prep."
              className="text-gray-600 max-w-2xl mx-auto"
              as="p"
            />
          </div>

          <div className="grid md:grid-cols-4 gap-6">
            {flowSteps.map((step, i) => (
              <div key={i} className="text-center">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center mx-auto mb-4 relative">
                  <step.icon className="w-6 h-6 text-violet-600" />
                  <span className="absolute -top-1 -right-1 w-6 h-6 bg-violet-500 text-white rounded-full text-xs flex items-center justify-center font-bold">
                    {step.num}
                  </span>
                </div>
                <EditableText
                  sectionId="lash-demo-flow"
                  field={`step${step.num}_title`}
                  defaultValue={step.title}
                  className="font-semibold mb-1 block"
                  as="h3"
                />
                <EditableText
                  sectionId="lash-demo-flow"
                  field={`step${step.num}_desc`}
                  defaultValue={step.desc}
                  className="text-sm text-gray-600"
                  as="p"
                />
                {i < flowSteps.length - 1 && (
                  <div className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2">
                    <ArrowRight className="w-4 h-4 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-gray-500 italic">
              <EditableText
                sectionId="lash-demo-flow"
                field="note"
                defaultValue="Phygital handles the preference layer. Payments and scheduling stay with your existing system (Acuity, Square, etc.)"
                className="inline"
              />
            </p>
          </div>
        </div>
      </EditableSection>

      {/* Style Gallery - EDITABLE IMAGES */}
      <EditableSection sectionId="lash-demo-styles" label="Style Gallery" className="py-16 px-4 bg-gradient-to-b from-white to-violet-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <EditableText sectionId="lash-demo-styles" field="title" defaultValue="Your Lash Style Gallery" className="text-3xl font-bold mb-2 block" as="h2" />
            <EditableText sectionId="lash-demo-styles" field="subtitle" defaultValue="Clients browse your portfolio and select their desired look" className="text-gray-600" as="p" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            {lashStyles.map((style) => (
              <div key={style.id} className="group relative" data-testid={`lash-style-${style.id}`}>
                <EditableImage
                  sectionId="lash-demo-styles"
                  field={`style_${style.id}`}
                  defaultImage={null}
                  className="aspect-square rounded-xl overflow-hidden"
                  placeholderClassName="bg-gradient-to-br from-violet-100 to-purple-100"
                  placeholderIcon={<Eye className="w-8 h-8 text-violet-300" />}
                />
                <div className="absolute bottom-2 left-2 right-2">
                  <span className="text-xs font-medium bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full">
                    <EditableText sectionId="lash-demo-styles" field={`style_${style.id}_name`} defaultValue={style.name} className="inline" />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </EditableSection>

      {/* Built for Lash Techs */}
      <EditableSection sectionId="lash-demo-features" label="Features" className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <EditableText sectionId="lash-demo-features" field="title" defaultValue="Built for Lash Technicians" className="text-3xl font-bold mb-2 block" as="h2" />
            <EditableText sectionId="lash-demo-features" field="subtitle" defaultValue="Everything you need to prep better and serve faster" className="text-gray-600" as="p" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card key={i} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <Badge className="mb-3 bg-violet-100 text-violet-700">Ready</Badge>
                  <h3 className="font-semibold mb-2">
                    <EditableText sectionId="lash-demo-features" field={`feature_${i}_title`} defaultValue={feature.title} className="inline" />
                  </h3>
                  <p className="text-sm text-gray-600">
                    <EditableText sectionId="lash-demo-features" field={`feature_${i}_desc`} defaultValue={feature.desc} className="inline" />
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </EditableSection>

      {/* Client Preferences Section */}
      <EditableSection sectionId="lash-demo-prefs" label="Preferences" className="py-16 px-4 bg-gradient-to-b from-white to-violet-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <EditableText sectionId="lash-demo-prefs" field="title" defaultValue="Know Their Preferences Before They Arrive" className="text-3xl font-bold mb-2 block" as="h2" />
            <EditableText sectionId="lash-demo-prefs" field="subtitle" defaultValue="Detailed preferences collected automatically" className="text-gray-600" as="p" />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Eye Shape */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-violet-700">
                  <EditableText sectionId="lash-demo-prefs" field="eyeshape_title" defaultValue="Eye Shape" className="inline" />
                </h3>
                <div className="flex flex-wrap gap-2">
                  {eyeShapes.map(shape => (
                    <Badge key={shape} variant="outline" className="cursor-pointer hover:bg-violet-50">{shape}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Curl Type */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-purple-700">
                  <EditableText sectionId="lash-demo-prefs" field="curl_title" defaultValue="Curl Type" className="inline" />
                </h3>
                <div className="flex flex-wrap gap-2">
                  {curlTypes.map(curl => (
                    <Badge key={curl} variant="outline" className="cursor-pointer hover:bg-purple-50">{curl}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Length */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold mb-4 text-violet-700">
                  <EditableText sectionId="lash-demo-prefs" field="length_title" defaultValue="Length" className="inline" />
                </h3>
                <div className="flex flex-wrap gap-2">
                  {lengthOptions.map(len => (
                    <Badge key={len} variant="outline" className="cursor-pointer hover:bg-violet-50 text-xs">{len}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sensitivities Alert */}
          <Card className="mt-6 border-amber-200 bg-amber-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">
                    <EditableText sectionId="lash-demo-prefs" field="alert_title" defaultValue="Allergy & Sensitivity Alerts" className="inline" />
                  </h3>
                  <p className="text-sm text-amber-700">
                    <EditableText sectionId="lash-demo-prefs" field="alert_desc" defaultValue="Know about adhesive sensitivities, contact lenses, or eye conditions before the appointment - no surprises when they're on your table." className="inline" />
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </EditableSection>

      {/* Preference Card Mockup - "YOU SEE" */}
      <EditableSection sectionId="lash-demo-prefcard" label="Preference Card Preview" className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <EditableText sectionId="lash-demo-prefcard" field="title" defaultValue="What You See Before Each Client" className="text-3xl font-bold mb-2 block" as="h2" />
            <EditableText sectionId="lash-demo-prefcard" field="subtitle" defaultValue="Every client arrives with their preferences already shared" className="text-gray-600" as="p" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* YOU SEE card */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <p className="text-xs font-medium text-gray-500 mb-4">YOU SEE</p>
              <Card className="border">
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold">Client Name - Time</p>
                      <p className="text-sm text-violet-600">Classic Full Set</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Badge variant="outline" className="text-xs">Eye Shape</Badge>
                    <Badge variant="outline" className="text-xs">Natural Lash Length</Badge>
                    <Badge variant="outline" className="text-xs">Style Preference</Badge>
                  </div>
                  {/* Inspo image placeholders */}
                  <div className="grid grid-cols-3 gap-2">
                    <EditableImage
                      sectionId="lash-demo-prefcard"
                      field="inspo_1"
                      defaultImage={null}
                      className="aspect-square rounded-lg overflow-hidden"
                      placeholderClassName="bg-violet-50"
                      placeholderIcon={<ImageIcon className="w-5 h-5 text-violet-300" />}
                    />
                    <EditableImage
                      sectionId="lash-demo-prefcard"
                      field="inspo_2"
                      defaultImage={null}
                      className="aspect-square rounded-lg overflow-hidden"
                      placeholderClassName="bg-violet-50"
                      placeholderIcon={<ImageIcon className="w-5 h-5 text-violet-300" />}
                    />
                    <EditableImage
                      sectionId="lash-demo-prefcard"
                      field="inspo_3"
                      defaultImage={null}
                      className="aspect-square rounded-lg overflow-hidden"
                      placeholderClassName="bg-violet-50"
                      placeholderIcon={<ImageIcon className="w-5 h-5 text-violet-300" />}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* CLIENT VIEW card */}
            <div className="bg-gray-50 rounded-2xl p-6">
              <p className="text-xs font-medium text-gray-500 mb-4">CLIENT VIEW</p>
              <Card className="border">
                <CardContent className="p-4">
                  <p className="text-sm text-gray-600 mb-3">Share your lash preferences for your appointment</p>
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Eye Shape</p>
                      <div className="flex gap-2 flex-wrap">
                        {["Almond", "Round", "Hooded", "Monolid"].map(s => (
                          <span key={s} className={`px-2 py-1 rounded text-xs ${s === "Almond" ? "bg-violet-100 text-violet-700" : "bg-gray-100"}`}>{s}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Curl Type</p>
                      <div className="flex gap-2 flex-wrap">
                        {["C Curl", "D Curl", "CC Curl"].map(c => (
                          <span key={c} className={`px-2 py-1 rounded text-xs ${c === "C Curl" ? "bg-violet-100 text-violet-700" : "bg-gray-100"}`}>{c}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Upload Inspo</p>
                      <div className="border-2 border-dashed border-violet-200 rounded-lg p-3 text-center">
                        <ImageIcon className="w-5 h-5 text-violet-300 mx-auto mb-1" />
                        <p className="text-xs text-gray-400">Tap to add photos</p>
                      </div>
                    </div>
                  </div>
                  <p className="text-xs text-green-600 mt-3 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Preferences saved
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </EditableSection>

      {/* Service Menu WITH IMAGES */}
      <EditableSection sectionId="lash-demo-services" label="Service Menu" className="py-16 px-4 bg-gradient-to-b from-white to-violet-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <EditableText sectionId="lash-demo-services" field="title" defaultValue="Service Menu" className="text-3xl font-bold mb-2 block" as="h2" />
            <EditableText sectionId="lash-demo-services" field="subtitle" defaultValue="Clients see your services and know exactly what to expect" className="text-gray-600" as="p" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {services.map((service) => (
              <Card key={service.id} className="hover:shadow-md transition-shadow overflow-hidden" data-testid={`service-${service.id}`}>
                {/* Service Image */}
                <EditableImage
                  sectionId="lash-demo-services"
                  field={`service_img_${service.id}`}
                  defaultImage={null}
                  className="aspect-[4/3] w-full overflow-hidden"
                  placeholderClassName="bg-gradient-to-br from-violet-50 to-purple-50"
                  placeholderIcon={<ImageIcon className="w-6 h-6 text-violet-200" />}
                />
                <CardContent className="p-4 text-center">
                  <h3 className="font-semibold text-sm mb-1">
                    <EditableText sectionId="lash-demo-services" field={`service_name_${service.id}`} defaultValue={service.name} className="inline" />
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">
                    <EditableText sectionId="lash-demo-services" field={`service_dur_${service.id}`} defaultValue={service.duration} className="inline" />
                  </p>
                  <p className="text-violet-600 font-medium">
                    <EditableText sectionId="lash-demo-services" field={`service_price_${service.id}`} defaultValue={service.price} className="inline" />
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </EditableSection>

      {/* CTA Section */}
      <EditableSection sectionId="lash-demo-cta" label="Bottom CTA" className="py-20 px-4 bg-gradient-to-r from-violet-500 to-purple-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <EditableText
            sectionId="lash-demo-cta"
            field="title"
            defaultValue="Ready to Know Their Look Before They Close Their Eyes?"
            className="text-3xl sm:text-4xl font-bold mb-6 block"
            as="h2"
          />
          <EditableText
            sectionId="lash-demo-cta"
            field="subtitle"
            defaultValue="Join the pilot program and get early access. Works with your existing booking system."
            className="text-white/90 text-lg mb-8 block"
            as="p"
          />
          <Button
            onClick={onAccept}
            size="lg"
            className="bg-white text-violet-600 hover:bg-gray-100 h-14 px-10 rounded-full text-lg font-semibold"
            data-testid="lash-bottom-cta"
          >
            <EditableText sectionId="lash-demo-cta" field="button_text" defaultValue="Accept Invite" className="inline" />
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <p className="mt-4 text-white/70 text-sm">
            <EditableText sectionId="lash-demo-cta" field="fine_print" defaultValue="Free during pilot - No credit card required" className="inline" />
          </p>
        </div>
      </EditableSection>
    </div>
  );
}
